import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Rocket, CircleCheck, Clock, Gauge, CalendarClock } from "lucide-react";

const C = { green: "#1D9E75", blue: "#1F75A8", amber: "#BA7517", red: "#C0392B" };

function Stat({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ minWidth: 140 }}>
      <div style={{ fontSize: 11, opacity: 0.6, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 3, color: color || "inherit" }}>{value}</div>
    </div>
  );
}

export default function LaunchStatusCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/launch/status").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;

  const live = s.phase === "live";
  const accent = live ? C.green : C.blue;

  return (
    <div data-testid="launch-status-card" className="card"
         style={{ marginBottom: 18, borderLeft: `5px solid ${accent}`,
                  background: "linear-gradient(135deg, #ffffff 0%, #F5FAFF 100%)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {live ? <Rocket size={18} color={C.green} /> : <CalendarClock size={18} color={C.blue} />}
        <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: ".08em", opacity: 0.6 }}>Executive Launch Status</span>
        <span className="spacer" />
        <span data-testid="launch-phase-badge"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: live ? "#E6F7F0" : "#EAF3FB",
                       color: accent, padding: "5px 12px", borderRadius: 16, fontSize: 12, fontWeight: 800 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: accent }} />
          {live ? "LIVE" : "Launch Readiness Complete"}
        </span>
      </div>

      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 10 }}>
        {live ? "Commercial Operations Active" : s.countdown_text}
      </div>

      <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 16 }}>
        <Stat label="Official Business Launch" value={s.launch_date} testid="launch-date-stat" />
        {live ? (
          <>
            <Stat label="Business Age" value={s.business_age.label} color={C.green} testid="business-age-stat" />
            <Stat label="Today's Revenue" value={s.today.revenue} testid="today-revenue-stat" />
            <Stat label="Today's Customers" value={s.today.customers} testid="today-customers-stat" />
            <Stat label="Today's Conversions" value={s.today.conversions} testid="today-conversions-stat" />
            <Stat label="Executive Reporting" value={s.executive_reporting} color={C.green} />
          </>
        ) : (
          <>
            <Stat label="Days Until Launch" value={s.days_until_launch} color={C.blue} testid="days-until-stat" />
            <Stat label="Technical Certification" value={`✅ ${s.technical_certification.label}`} color={C[s.technical_certification.color]} />
            <Stat label="Operational Readiness" value="✅ Complete" color={C.green} />
            <Stat label="Commercial Status" value="⏳ Pending Day Zero" color={C.amber} />
          </>
        )}
      </div>

      <div style={{ marginTop: 14, padding: "10px 14px", background: "#F8FAFC", borderRadius: 8, fontSize: 13 }}>
        <span style={{ fontWeight: 700, color: accent }}>Next Executive Action: </span>
        {s.next_executive_action}
      </div>
      {!live && (
        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.65 }}>
          <Gauge size={12} style={{ verticalAlign: "-1px" }} /> {s.reporting_status}
        </div>
      )}
    </div>
  );
}
